import {
  isValidHex, hexToRgbList, shadeHex, mixHex, ensureContrast,
} from './colorUtil';
import { AVAILABILITY_IMAGE_THEME_FALLBACK } from '../constants/bookingConst';

// What the dark designs' near-black is blended from, and how far.
const DARK_BASE = '#05080a';
const WHITE = '#ffffff';
const BLACK = '#000000';

/**
 * Builds the availability images' colours from the club's theme
 * (Config -> Theme), so every image carries the club's own colour rather
 * than the mockups' fixed lime/green.
 *
 * Everything is derived from the Active Button Color: the dark designs get a
 * near-black tinted with it and the colour itself as the neon accent; the
 * light designs a pale tint of it with a deeper shade for text and buttons.
 * The timeslot table takes the guest grid's own theme colours (text colour
 * and the active colour's deep shade for promos), as the guest page does.
 * Accents are pushed lighter or darker until they read on their
 * background, so a very pale or very dark club colour still works.
 *
 * @param {object} config - club config (themeActiveColor, themeTextColor)
 * @returns {{accentOnDark: string, accentOnLight: string, cssVars: Object<string, string>}}
 *   accentOnDark / accentOnLight for icons drawn with a colour prop (SVG
 *   attributes can't read CSS variables); cssVars to set on the template root.
 */
export const buildAvailabilityPalette = (config) => {
  const accent = isValidHex(config?.themeActiveColor)
    ? config.themeActiveColor.trim()
    : AVAILABILITY_IMAGE_THEME_FALLBACK.activeColor;
  const ink = isValidHex(config?.themeTextColor)
    ? config.themeTextColor.trim()
    : AVAILABILITY_IMAGE_THEME_FALLBACK.textColor;

  // ---- dark designs ----
  const darkBg = mixHex(accent, DARK_BASE, 0.9);
  const darkBg2 = mixHex(accent, DARK_BASE, 0.88);
  const accentOnDark = ensureContrast(accent, darkBg, 7, WHITE);
  // Text on the neon button: dark if it reads, otherwise white.
  const onAccentDark = ensureContrast(darkBg, accentOnDark, 4.5, BLACK);

  // ---- light designs ----
  const lightBg = mixHex(accent, WHITE, 0.95);
  // Text-sized accent on white / pale tint (titles, counts, the button).
  const accentOnLight = ensureContrast(accent, WHITE, 4.5, BLACK);

  const cssVars = {
    '--at-neon': accentOnDark,
    '--at-neon-rgb': hexToRgbList(accentOnDark),
    '--at-neon-hi': mixHex(accentOnDark, WHITE, 0.25),
    '--at-neon-lo': mixHex(accentOnDark, BLACK, 0.3),
    '--at-on-neon': onAccentDark,
    '--at-dark-bg': darkBg,
    '--at-dark-bg-2': darkBg2,
    '--at-dark-bg-rgb': hexToRgbList(darkBg),
    '--at-dark-glow-rgb': hexToRgbList(mixHex(accent, BLACK, 0.55)),
    '--at-court-1': mixHex(accent, BLACK, 0.62),
    '--at-court-2': mixHex(accent, BLACK, 0.74),
    '--at-court-3': mixHex(accent, BLACK, 0.82),

    '--at-leaf': accentOnLight,
    '--at-leaf-rgb': hexToRgbList(accentOnLight),
    '--at-light-bg': lightBg,
    '--at-light-bg-2': mixHex(accent, WHITE, 0.91),
    '--at-light-shape-1': mixHex(accent, WHITE, 0.76),
    '--at-light-shape-2': mixHex(accent, WHITE, 0.85),
    '--at-shadow-rgb': hexToRgbList(mixHex(accent, BLACK, 0.7)),

    // Guest-grid table (AvailabilityGuestGrid.css), as the guest page sets it.
    '--at-ink': ink,
    '--at-ink-rgb': hexToRgbList(ink),
    '--at-promo': shadeHex(accent, -12),
    '--at-promo-rgb': hexToRgbList(accent),
  };

  return { accentOnDark, accentOnLight, cssVars };
};
